import type { EmblaOptionsType, EmblaPluginType } from 'embla-carousel';
import type { VNode } from 'vue';

import emblaCarouselVue from 'embla-carousel-vue';
import { Comment, Fragment, markRaw } from 'vue';

interface UseCarouselOptions {
  options:
    | ComputedRef<EmblaOptionsType>
    | Ref<EmblaOptionsType>
    | EmblaOptionsType;
  plugins?:
    | ComputedRef<EmblaPluginType[]>
    | Ref<EmblaPluginType[]>
    | EmblaPluginType[];
}

interface UseCarouselReturn {
  emblaRef: Ref<HTMLElement | undefined>;
  emblaApi: Ref;
  selectedIndex: Ref<number>;
  scrollSnaps: Ref<number[]>;
  canScrollPrev: Ref<boolean>;
  canScrollNext: Ref<boolean>;
  slides: ComputedRef<VNode[]>;
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
}

export const useCarousel = function ({
  options,
  plugins,
}: UseCarouselOptions): UseCarouselReturn {
  const slots = useSlots();

  // Initialize embla carousel
  const [emblaRef, emblaApi] = emblaCarouselVue(options, plugins);

  // Carousel state
  const selectedIndex = ref(0);
  const scrollSnaps = ref<number[]>([]);
  const canScrollPrev = ref(false);
  const canScrollNext = ref(false);

  // Recursively flatten fragment nodes (v-for, template, nested slots)
  const flattenFragments = (vnodes: VNode[]): VNode[] => {
    return vnodes.flatMap((vnode: VNode) => {
      if (vnode.type === Fragment && Array.isArray(vnode.children)) {
        return flattenFragments(vnode.children as VNode[]);
      }
      return vnode;
    }).filter((vnode: VNode) => vnode && vnode.type !== Comment);
  };

  // Get slides from slot
  const slides = computed(() => {
    const defaultSlot = slots.default?.();
    if (!defaultSlot) {
      return [];
    }

    return flattenFragments(defaultSlot).map(vnode => markRaw(vnode));
  });

  // Update carousel state
  const updateState = () => {
    if (!emblaApi.value) {
      return;
    }

    scrollSnaps.value = emblaApi.value.scrollSnapList();
    selectedIndex.value = emblaApi.value.selectedScrollSnap();
    canScrollPrev.value = emblaApi.value.canScrollPrev();
    canScrollNext.value = emblaApi.value.canScrollNext();
  };

  // Navigation methods
  const scrollPrev = () => emblaApi.value?.scrollPrev();
  const scrollNext = () => emblaApi.value?.scrollNext();
  const scrollTo = (index: number) => emblaApi.value?.scrollTo(index);

  // Watch for slides changes and reinitialize
  watch(
    () => slides.value.length,
    () => {
      if (emblaApi.value) {
        void nextTick(() => {
          emblaApi.value?.reInit();
        });
      }
    },
  );

  // Setup event listeners
  onMounted(() => {
    if (!emblaApi.value) {
      return;
    }

    // Listen to carousel events
    emblaApi.value.on('init', updateState);
    emblaApi.value.on('reInit', updateState);
    emblaApi.value.on('select', updateState);

    // Initialize state after DOM is ready
    void nextTick(() => {
      updateState();
    });
  });

  onUnmounted(() => {
    if (!emblaApi.value) {
      return;
    }

    emblaApi.value.off('init', updateState);
    emblaApi.value.off('reInit', updateState);
    emblaApi.value.off('select', updateState);
  });

  return {
    emblaRef,
    emblaApi,
    selectedIndex,
    scrollSnaps,
    canScrollPrev,
    canScrollNext,
    slides,
    scrollPrev,
    scrollNext,
    scrollTo,
  };
};
